export interface HsvColour {
  h: number;
  s: number;
  v: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function hexToHsv(hex: string): HsvColour {
  const normalized = /^#[0-9a-f]{6}$/i.test(hex) ? hex.slice(1) : 'ffffff';
  const red = parseInt(normalized.slice(0, 2), 16) / 255;
  const green = parseInt(normalized.slice(2, 4), 16) / 255;
  const blue = parseInt(normalized.slice(4, 6), 16) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;
  if (delta) {
    if (max === red) hue = 60 * (((green - blue) / delta) % 6);
    else if (max === green) hue = 60 * (((blue - red) / delta) + 2);
    else hue = 60 * (((red - green) / delta) + 4);
  }
  if (hue < 0) hue += 360;
  return {
    h: hue,
    s: max ? delta / max : 0,
    v: max,
  };
}

export function hsvToHex({ h, s, v }: HsvColour): string {
  const hue = ((h % 360) + 360) % 360;
  const saturation = clamp(s, 0, 1);
  const value = clamp(v, 0, 1);
  const chroma = value * saturation;
  const section = hue / 60;
  const component = chroma * (1 - Math.abs((section % 2) - 1));
  const [red, green, blue] = section < 1
    ? [chroma, component, 0]
    : section < 2
      ? [component, chroma, 0]
      : section < 3
        ? [0, chroma, component]
        : section < 4
          ? [0, component, chroma]
          : section < 5
            ? [component, 0, chroma]
            : [chroma, 0, component];
  const offset = value - chroma;
  return `#${[red, green, blue].map(channel => (
    Math.round((channel + offset) * 255).toString(16).padStart(2, '0')
  )).join('')}`;
}
